<template>
  <div :class="['banner-top-root', (!isShowBanner || !bannerList.length) && ['banner-hide']]">
    <template v-if="!isBannerInit">
      <bannerTopSkeleton></bannerTopSkeleton>
    </template>
    <template v-if="bannerList.length && isShowBanner">
      <div
        v-for="banner in bannerList"
        :key="banner.sequence"
        class="imgs__wrap"
        :style="bannerStyle"
        @click="onImgClick(banner)"
      >
        <lc-img class="banner-img img__left" :imgUrl="banner.image_left" :showErrorImg="false"></lc-img>
        <lc-img
          class="banner-img img__center"
          :imgUrl="banner.image_url"
          :showErrorImg="false"
          :imgLazyLoad="false"
        ></lc-img>
        <lc-img class="banner-img img__right" :imgUrl="banner.image_right" :showErrorImg="false"></lc-img>
      </div>
      <IconMyBannerIconClose v-if="isShowIconClose" class="icon close" @click="hideBanner"></IconMyBannerIconClose>
      <el-popover
        placement="right"
        effect="light"
        :width="106"
        popperClass="banner-dr-code-pop"
        @show="onHelpQrCodeShow"
      >
        <template #reference>
          <div class="help-qrcode-label">立即咨询</div>
        </template>
        <template #default><help-qrcode></help-qrcode></template>
      </el-popover>
    </template>
  </div>
</template>

<script setup lang="ts">
import { usePUserStore } from '@/store';
import { openInNewTab } from '@/utils/open-in-new-tab';
import { RespBanner } from '@vas2t-lowcode/api';
import { computed, defineAsyncComponent, onMounted, ref, watchEffect } from 'vue';
import { dwBannerClick, dwBannerCloseClick, dwBannerQrCodeHover, dwBannerShow } from './banner-top-dw';
import { reqBannerTopDataAsync, resetBannerTopDataAsync } from './banner-top-interface';
import bannerTopSkeleton from './banner-top-skeleton.vue';
import { getBannerShowStateFromStorage, setBannerShowStateToStorage } from './banner-top.tools';
import LcImg from '@/features/lc-img/lc-img.vue';

// const lcImg = defineAsyncComponent(() => import('@/features/lc-img/lc-img.vue'));

const HelpQrcode = defineAsyncComponent(() => import('@/features/help-icon/help-qrcode.vue'));

/** banner 关闭按钮是否显示 */
const isShowIconClose = ref(false);

/**
 * 图片列表
 */
const bannerList = ref<Array<RespBanner>>([]);

const userStore = usePUserStore();

const isBannerDataLoading = ref(false);

/** 样式 */
const bannerStyle = computed(() => {
  const defStyle = {
    opacity: 1,
  };
  // 处理骨架屏加载状态变更
  if (!isBannerInit.value) {
    defStyle.opacity = 0;
  }
  return defStyle;
});

/** banner显示埋点 */
dwBannerShow();

/**
 * 更新图片列表
 */
const setImgList = () => {
  if (isBannerDataLoading.value) {
    return;
  }

  isBannerDataLoading.value = true;

  return reqBannerTopDataAsync()
    ?.then(({ data }) => {
      isShowIconClose.value = !!data.is_banners_closeable;

      let { banners = [] } = data;
      if (banners.length) {
        // 排序
        banners = banners.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        // 更新banner数据
        bannerList.value = banners;
      }
    })
    .catch(() => {
      bannerList.value.splice(0);
    })
    .finally(() => {
      isBannerDataLoading.value = false;
      resetBannerTopDataAsync();
    });
};

/**
 * 隐藏banner
 */
const hideBanner = () => {
  const isShow = false;
  isShowBanner.value = isShow;

  // 仅记住已登录用户
  userid.value && setBannerShowStateToStorage(userid.value, isShow);

  dwBannerCloseClick();
};

/**
 * 图片点击
 */
const onImgClick = (data: RespBanner) => {
  dwBannerClick();
  const { url } = data;
  url && openInNewTab({ url });
};

/** banner loading 中：默认展示-> 不显示or图片加载结束 */
const isBannerInit = ref(false);
/** bannerTop 骨架屏 */
// const bannerTopSkeleton = defineAsyncComponent(() => import('./banner-top-skeleton.vue'));

/** 是否显示banner */
const isShowBanner = ref(true);

const userid = computed(() => {
  return userStore?.userInfo?.userid || '';
});
/** 用户信息是否初始化完成 */
const isUserinfoReady = computed(() => {
  return !!userStore?.isReady;
});

/** 根据登录信息获取banner显示状态 */
const updateIsBannerShow = () => {
  if (userid.value) {
    // 获取用户配置的是否暂时banner
    const isShow = getBannerShowStateFromStorage(userid.value);
    isShowBanner.value = isShow;
  }
};

/**
 * 预加载图片
 */
const preloadImgs = (imgList: string[]) => {
  const promiseList = [] as Promise<boolean>[];
  for (let index = 0; index < (imgList || []).length; index++) {
    const imgPath = imgList[index];
    if (!imgPath) {
      continue;
    }
    const promiseImg = new Promise<boolean>((res) => {
      const img = new Image();
      img.onload = () => {
        res(true);
      };
      img.onerror = () => {
        res(false);
      };

      img.src = imgPath;
    });
    promiseList.push(promiseImg);
  }

  return Promise.all(promiseList);
};

watchEffect(() => {
  // 等待用户信息初始化完成
  const _userid = userid.value;
  if (!isUserinfoReady.value && !_userid) {
    return;
  }
  if (!_userid) {
    // 未登录状态默认显示 banner
    isShowBanner.value = true;
  } else {
    updateIsBannerShow();
  }
});

const preloadImg = () => {
  // 首屏的图片
  const imgs1st = bannerList.value[0];
  const { image_left, image_right, image_url } = imgs1st || {};
  return preloadImgs([image_left || '', image_right || '', image_url || '']).finally(() => {
    isBannerInit.value = true;
  });
};

/**
 * 立即咨询显示事件
 */
const onHelpQrCodeShow = () => {
  dwBannerQrCodeHover();
};

onMounted(async () => {
  await setImgList();
  preloadImg();
});
</script>

<style lang="scss" scoped>
.banner-top-root {
  min-width: var(--lowcode-min-width);
  margin: 24px $lowcode-root-padding 0 $lowcode-root-padding;
  height: 160px;
  position: relative;
  // transition: all 0.25s ease-in-out;
  flex: none;

  &.banner-hide {
    height: 0;
    margin-top: 0;
  }

  :deep(.img__root) {
    width: auto;
  }

  .imgs__wrap {
    height: 100%;
  }

  .img__center {
    :deep(img) {
      object-fit: fill;
      width: 100% !important;
    }
  }

  .img {
    &s__wrap {
      position: relative;
      height: 100%;
      border-radius: 8px;
      overflow: hidden;
      width: 100%;
    }

    &__center {
      width: 100%;
      height: 100%;
      z-index: 0;
    }

    &__left {
      top: 38px;
      z-index: 2;
    }

    &__right {
      z-index: 1;
    }
  }

  .icon {
    &.close {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 16px;
      height: 16px;
      cursor: pointer;
      z-index: 1;

      &:hover {
        :deep(path) {
          fill: #444e63;
        }
      }
      &:active {
        :deep(path) {
          fill: #727e96;
        }
      }
    }
  }

  .help-qrcode-label {
    cursor: pointer;
    display: inline-block;
    width: 64px;
    height: 22px;
    background: rgba(255, 255, 255, 1);
    border-radius: 4px;
    line-height: 22px;
    text-align: center;
    color: rgba(65, 127, 249, 1);
    position: absolute;
    left: 61px;
    top: 98px;
    font-size: 12px;
    z-index: 6;

    &:hover {
      opacity: 0.9;
    }
  }

  .banner-img {
    position: absolute;
    top: 0;

    :deep(.img) {
      width: auto;
    }
  }

  //  1058 < 1144 < 1218 < 1698
  @media screen and (min-width: 1058px) and (max-width: 1144px), screen and (max-width: 1058px) {
    // 1280px
    .img__left,
    .help-qrcode-label {
      left: 40px;
    }

    .img__right {
      left: 330px;
    }
  }

  @media screen and (min-width: 1144px) and (max-width: 1218px) {
    // 1366
    .img__left,
    .help-qrcode-label {
      left: 55px;
    }

    .img__right {
      left: 345px;
    }
  }

  @media screen and (min-width: 1218px) and (max-width: 1698px) {
    // 1440
    .img__left,
    .help-qrcode-label {
      left: 114px;
    }

    .img__right {
      left: 405px;
    }
  }

  @media screen and (min-width: 1698px) {
    // 1920
    .img__left,
    .help-qrcode-label {
      left: 280px;
    }

    .img__right {
      left: 686px;
    }
  }
}
</style>

<style lang="scss">
.el-popover.el-popper.banner-dr-code-pop {
  padding: 8px 8px 6px 8px;
  min-width: 30px;
  height: 146px;
  box-sizing: border-box;

  .help-qrcode__img {
    width: 90px;
    height: 90px;
    margin-bottom: 0;
  }

  .help-qrcode__label {
    color: var(--lowcode-text-03);
    // width: 144px;
    font-size: 12px;
    // line-height: 16px;
    margin-top: 8px;
    &:last-of-type {
      margin-top: 0;
    }
  }
}
</style>
